import React, { useEffect, useRef, useState } from "react";
import { toast } from "../../Utills/toast";
import PrintBill from "../../component/PrintBill/PrintBill";
import PaymentAllocationEditor, { createPaymentAllocation, getPaymentValidationError, summarizePayments } from "../../component/Billing/PaymentAllocationEditor";
import { createTakeawayBill, getTakeawayBill, settleTakeawayBill } from "./takeawayApi";
import { ButtonLoader, SectionLoader } from "../../component/common/Loader";
import { useOnlineStatus } from "../../Utills/useOnlineStatus";
import { useTrackedPrint } from "../../Utills/useTrackedPrint";
import { usePaymentMethodRequirement } from "../../Utills/usePaymentMethodRequirement";

const TakeawayBillingModal = ({ order, onClose, onUpdated }) => {
  const requirePaymentMethod = usePaymentMethodRequirement();
  const online = useOnlineStatus();
  const printRef = useRef();
  const print = useTrackedPrint({ contentRef: printRef, documentType: "BILL", getDocumentId: () => details?.bill?.billId || order.takeawayOrderId });
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ applyServiceCharge: false, discountAmount: "" });
  const [payments, setPayments] = useState([createPaymentAllocation(0)]);

  useEffect(() => {
    getTakeawayBill(order.takeawayOrderId).then(setDetails).catch(error => toast.error(error.message)).finally(() => setLoading(false));
  }, [order.takeawayOrderId]);
  const update = (name, value) => setForm(current => ({ ...current, [name]: value }));

  const generate = async event => {
    event.preventDefault(); setSaving(true);
    try {
      const result = await createTakeawayBill(order.takeawayOrderId, {
        paymentMode: order.paymentMode === "Online" ? "Cash" : order.paymentMode || "Cash",
        markPaid: false,
        applyServiceCharge: form.applyServiceCharge,
        discountAmount: Number(form.discountAmount || 0),
        settlementAmount: null
      });
      setDetails(result); toast.success("Takeaway bill generated."); onUpdated();
    } catch (error) { toast.error(error.message); }
    finally { setSaving(false); }
  };

  const settle = async event => {
    event.preventDefault();
    const summary = summarizePayments(payments);
    const paymentError = getPaymentValidationError(payments, details.payableAmount, requirePaymentMethod);
    if (paymentError) return toast.error(paymentError);
    setSaving(true);
    try {
      const result = await settleTakeawayBill(order.takeawayOrderId, {
        paymentMode: summary.paymentMode || "Unspecified",
        settlementAmount: summary.settlementAmount,
        payments: summary.payments
      });
      setDetails(result); toast.success("Payment completed."); onUpdated();
    } catch (error) { toast.error(error.message); }
    finally { setSaving(false); }
  };

  const bill = details?.bill;
  const fulfillmentLabel = order.fulfillmentType === 1 ? "Dine-in" : "Pick-up";
  const printData = details ? { orders: details.items, charges: { gstNumber: details.gstNumber || "" } } : { orders: [] };

  return <div className="takeaway-modal-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}><div className="takeaway-billing-modal">
    <div className="takeaway-modal-title"><div><h2>Billing · {order.tokenNumber}</h2><p>{fulfillmentLabel} · {order.customerName || "Walk-in customer"}</p></div><button onClick={onClose}>×</button></div>
    {loading ? <SectionLoader label="Loading bill..." rows={3} compact /> : !details ? <form className="takeaway-bill-form" onSubmit={generate}>
      <div className="takeaway-payment-details">
        <label>Discount amount<input type="number" min="0" step="0.01" value={form.discountAmount} onChange={event => update("discountAmount", event.target.value)} /></label>
        <label>Apply service charge<select value={form.applyServiceCharge ? "yes" : "no"} onChange={event => update("applyServiceCharge", event.target.value === "yes")}><option value="no">No</option><option value="yes">Yes</option></select></label>
      </div>
      <button className="takeaway-primary" disabled={saving || !online} title={!online ? "Internet connection is required to generate a bill." : undefined} aria-busy={saving}>{saving ? <ButtonLoader label="Generating..." /> : "Generate bill"}</button>
    </form> : <div className="takeaway-bill-view">
      <div className="takeaway-bill-summary"><span>Bill #{bill.billNumber}</span><strong>₹{Number(details.payableAmount).toFixed(2)}</strong><span className={bill.isPaid ? "paid" : "pending"}>{bill.isPaid ? "Paid" : "Payment pending"}</span></div>
      <table><thead><tr><th>Item</th><th>Qty</th><th>Total</th></tr></thead><tbody>{details.items.map(item => <tr key={item.billItemId}><td>{item.name}</td><td>{item.quantity}</td><td>₹{Number(item.totalPrice).toFixed(2)}</td></tr>)}</tbody></table>
      {!bill.isPaid && <form className="takeaway-settle" onSubmit={settle}><PaymentAllocationEditor payableAmount={details.payableAmount} payments={payments} onChange={setPayments} disabled={saving || !online} required={requirePaymentMethod} /><button className="takeaway-primary" disabled={saving || !online} title={!online ? "Internet connection is required to complete payment." : undefined} aria-busy={saving}>{saving ? <ButtonLoader label="Processing..." /> : "Complete payment"}</button></form>}
      {bill.payments?.length > 0 && <div className="takeaway-payment-breakdown">{bill.payments.map(payment => <div key={payment.billPaymentId}><span>{payment.paymentMode}</span><strong>₹{Number(payment.amount).toFixed(2)}</strong></div>)}</div>}
      <button className="takeaway-primary" onClick={print}>Print bill</button>
      <div className="takeaway-print"><PrintBill ref={printRef} OrderAndChargesDetails={printData} tableName={order.tokenNumber} subTotal={Number(bill.subTotal || 0)} packagingCharge={Number(bill.packagingCharge || 0)} cgstAmount={Number(bill.cgstAmount || 0)} sgstAmount={Number(bill.sgstAmount || 0)} serviceChargeAmount={Number(bill.serviceChargeAmount || 0)} appliedDiscount={Number(bill.discountAmount || 0)} grandTotal={Number(bill.grandTotal || 0)} orderMode={order.fulfillmentType === 1 ? "DINE-IN" : "PICK-UP"} billDetails={bill} roundOff={Number(details.roundOff || 0)} payableAmount={Number(details.payableAmount || 0)} /></div>
    </div>}
  </div></div>;
};

export default TakeawayBillingModal;
